import { consoleRoutes } from "@sagot/constants";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { parseBookingFilter, type BookingFilter } from "./bookings-model";
import type { LeadFilters, LeadsView } from "./lead-types";

const SEARCH_DEBOUNCE_MS = 300;
const ALL = "all";

const BOOKINGS = "bookings";

function parseView(value: string | null): LeadsView {
  if (value === "board") return "board";
  return value === BOOKINGS ? BOOKINGS : "table";
}

/** URL state of the leads page: `/leads/:leadId?` + `?view=board|bookings&stage=…&interest=…&status=…`. */
export function useLeadsRoute() {
  const { summary } = useWorkspaceContext();
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = parseView(params.get("view"));
  const bookingFilter = parseBookingFilter(params.get("status"));
  const stage = params.get("stage") ?? ALL;
  const interest = params.get("interest");
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(query.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [query]);

  const setParam = useCallback(
    (key: string, value: string | null) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value === null) next.delete(key);
          else next.set(key, value);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const search = params.toString();
  const leadHref = useCallback((id: string) => `${consoleRoutes.leads(summary.slug, id)}${search ? `?${search}` : ""}`, [summary.slug, search]);
  const closeLead = useCallback(() => {
    void navigate(`${consoleRoutes.leads(summary.slug)}${search ? `?${search}` : ""}`);
  }, [navigate, summary.slug, search]);

  const filters: LeadFilters = { stageId: stage === ALL ? null : stage, q: debounced, interest };
  return {
    leadId: leadId ?? null,
    view,
    stage,
    filters,
    query,
    setQuery,
    bookingFilter,
    setView: (next: LeadsView) => {
      setParams(
        (current) => {
          const nextParams = new URLSearchParams(current);
          if (next === "table") nextParams.delete("view");
          else nextParams.set("view", next);
          if (next !== BOOKINGS) nextParams.delete("status");
          return nextParams;
        },
        { replace: true },
      );
    },
    setBookingFilter: (next: BookingFilter) => {
      setParam("status", next === "all" ? null : next);
    },
    setStage: (next: string) => {
      setParam("stage", next === ALL ? null : next);
    },
    setInterest: (next: string | null) => {
      setParam("interest", next);
    },
    clearFilters: () => {
      setQuery("");
      setParams(view === "table" ? {} : { view }, { replace: true });
    },
    leadHref,
    closeLead,
  };
}

export const ALL_STAGES = ALL;
